import { useRef } from "react";


/**
 * React hook that returns a filtered subset of a given `value` object,
 * based on an array of `filters`. The result is memoized between renders
 * using a `useRef` to prevent unnecessary updates when values haven't changed.
 *
 * @param {Object} value - The source object containing properties to filter.
 * @param {Array<string>} filters - An array of property names to retain from `value`.
 * @returns {Object} - A memoized object containing only the filtered key-value pairs.
 */
function useFilteredProps(value, filters){
  value = value || {};
  const ref = useRef();

  if (!ref.current) {
    ref.current = {
      propset: new Set(), 
      value: {}
    };
  }

  const {
    propset: currentPropset,
    value: currentValue,
  } = ref.current;

  const filtersSet = new Set(filters || []);
  const anyPropChanged = !setsAreEqual(currentPropset, filtersSet);
  let anyValueChanged = false;
  (filters || []).forEach(item => {
    if (value[item] !== currentValue[item]) {
      anyValueChanged = true;
    }
  })

  if (anyPropChanged || anyValueChanged) {
    const newFilters = (filters || []).reduce((_, item) => {
      _[item] = value[item];
      return _;
    }, {});

    ref.current = {value: newFilters, propset: filtersSet};
  }

  return ref.current.value;
}


function setsAreEqual(a, b) {
  if (a.size !== b.size) return false;
    return a.values().every((val) => b.has(val));
}

export default useFilteredProps;